/**
 * Stack & Save — bundle builder interaction (sections/stack-and-save.liquid).
 *
 * This pass owns selection state only:
 *   - row checkbox toggles the row's selected styling
 *   - select-all checkbox drives every enabled row, and reflects their state
 *     (checked / unchecked / indeterminate)
 *   - variant <select> mirrors the chosen variant's price into the row price
 *     node and onto the row checkbox's value + data-stack-price
 *
 * The summary figures are computed by stack-and-save-pricing.js, which folds the
 * data hooks this module maintains (data-stack-price / data-stack-special-price
 * on each checkbox). Every branch below ends in a recompute.
 */

import recomputePricing from './stack-and-save-pricing.js';
import addSelectionToCart from './stack-and-save-cart.js';

const SELECTED_CLASS = 'is-selected';

/** Reads every enabled row checkbox in a section. */
const getRowCheckboxes = (root) =>
  Array.from(root.querySelectorAll('[data-stack-checkbox]')).filter((input) => !input.disabled);

/** Mirrors a row's checked state onto its wrapper for styling. */
const paintRow = (checkbox) => {
  const row = checkbox.closest('[data-stack-row]');
  if (!row) return;
  row.classList.toggle(SELECTED_CLASS, checkbox.checked);
};

/** Keeps the select-all box in sync with the rows below it. */
const syncSelectAll = (root) => {
  const selectAll = root.querySelector('[data-stack-select-all]');
  if (!selectAll) return;

  const checkboxes = getRowCheckboxes(root);
  const checkedCount = checkboxes.filter((input) => input.checked).length;

  selectAll.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
  selectAll.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
};

/**
 * Applies the chosen variant to its row: price text, checkbox value, and the
 * price data hooks the later math pass reads.
 */
const applyVariant = (select) => {
  const row = select.closest('[data-stack-row]');
  if (!row) return;

  const option = select.options[select.selectedIndex];
  if (!option) return;

  const priceNode = row.querySelector('[data-stack-row-price]');
  const formatted = option.dataset.stackPriceFormatted;
  if (priceNode && formatted) priceNode.textContent = formatted;

  const checkbox = row.querySelector('[data-stack-checkbox]');
  if (!checkbox) return;

  checkbox.value = option.value;
  checkbox.dataset.stackPrice = option.dataset.stackPrice || '';
  // fall back to the regular price so a missing attribute means zero discount, not NaN
  checkbox.dataset.stackSpecialPrice =
    option.dataset.stackSpecialPrice || option.dataset.stackPrice || '';
  checkbox.dataset.stackComparePrice = option.dataset.stackComparePrice || '';

  // A sold-out variant can't be part of the stack — drop it from the selection.
  if (option.disabled) {
    checkbox.checked = false;
    checkbox.disabled = true;
  } else {
    checkbox.disabled = false;
  }

  paintRow(checkbox);
};

/** Wires one section instance. */
const initSection = (root) => {
  if (root.dataset.stackInit === 'true') return;
  root.dataset.stackInit = 'true';

  getRowCheckboxes(root).forEach(paintRow);
  syncSelectAll(root);
  recomputePricing(root);

  root.addEventListener('change', (event) => {
    const target = event.target;

    if (target.matches('[data-stack-select-all]')) {
      getRowCheckboxes(root).forEach((checkbox) => {
        checkbox.checked = target.checked;
        paintRow(checkbox);
      });
    } else if (target.matches('[data-stack-checkbox]')) {
      paintRow(target);
    } else if (target.matches('[data-stack-variant-select]')) {
      applyVariant(target);
    } else {
      return;
    }

    syncSelectAll(root);
    recomputePricing(root);
  });

  root.addEventListener('click', (event) => {
    if (event.target.closest('[data-stack-submit]')) addSelectionToCart(root);
  });
};

export default function initStackAndSave() {
  document.querySelectorAll('[data-stack-and-save]').forEach(initSection);
}
